import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { postJson } from '~/lib/http';
import type { EventDto } from '~/lib/types';
import { AREA_LABELS } from '~/lib/labels';
import { filtersToHomeQuery, type SearchFilters } from '~/lib/search-nav';

interface OrbSearchResponse {
  events: EventDto[];
  filters: SearchFilters;
  interpretation: string;
  aiUsed: boolean;
  widened?: boolean;
}

/**
 * Floating event-finder concierge. A small glowing orb, bottom-right on every
 * public page. Opens a compact panel: type what you're looking for → POST
 * /api/ai/search → a short list of matching events + the interpretation line +
 * "See all results" into the filtered calendar. Pure finder — reuses the same
 * search endpoint the hero bar uses; invents no backend.
 */
export function AiOrb() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<OrbSearchResponse | null>(null);
  const [seeAllHref, setSeeAllHref] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const orbRef = useRef<HTMLButtonElement>(null);

  // Focus the input when the panel opens; Esc closes and returns focus.
  useEffect(() => {
    if (!open) return;
    inputRef.current?.focus();
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        orbRef.current?.focus();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  async function run(e: React.FormEvent) {
    e.preventDefault();
    const text = query.trim();
    if (!text || pending) return;
    setPending(true);
    setError(null);
    const res = await postJson<OrbSearchResponse>('/api/ai/search', { query: text });
    setPending(false);
    if (!res.ok) {
      setError("Couldn't search just now — try the calendar filters.");
      setResult(null);
      setSeeAllHref(null);
      return;
    }
    setResult(res.data);
    setSeeAllHref(
      `/?${filtersToHomeQuery(res.data.filters, text, res.data.interpretation, Boolean(res.data.widened))}`
    );
  }

  const events = result?.events ?? [];

  return (
    <div className="ai-orb-root">
      {open && (
        <div
          ref={panelRef}
          className="ai-orb-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Find an event"
        >
          <div className="ai-orb-panel-head">
            <span className="ai-orb-panel-title">Find an event</span>
            <button
              type="button"
              className="ai-orb-close"
              aria-label="Close event finder"
              onClick={() => {
                setOpen(false);
                orbRef.current?.focus();
              }}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" aria-hidden="true">
                <path
                  d="M5 5l10 10M15 5L5 15"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>

          <form className="ai-orb-form" onSubmit={run}>
            <label className="sr-only" htmlFor="ai-orb-input">
              What are you looking for?
            </label>
            <input
              id="ai-orb-input"
              ref={inputRef}
              className="ui-input"
              type="search"
              placeholder="e.g. free tech meetups in St. Pete"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={pending}
            />
            <button type="submit" className="ui-btn btn-primary btn-sm" disabled={pending}>
              {pending ? '…' : 'Find'}
            </button>
          </form>

          <div className="ai-orb-body" aria-live="polite">
            {error && <p className="form-error small">{error}</p>}

            {result && (
              <>
                {result.interpretation && (
                  <p className="ai-orb-interp small">{result.interpretation}</p>
                )}
                {events.length === 0 ? (
                  <p className="small muted">
                    Nothing matched. Try fewer words, or browse the calendar.
                  </p>
                ) : (
                  <ul className="ai-orb-results">
                    {events.slice(0, 6).map((ev) => (
                      <li key={ev.id}>
                        <Link
                          to={`/events/${ev.slug}`}
                          className="ai-orb-result"
                          onClick={() => setOpen(false)}
                        >
                          <span className="ai-orb-result-title">{ev.title}</span>
                          <span className="ai-orb-result-meta t-num">
                            {ev.dateLabel} · {AREA_LABELS[ev.area]}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                {seeAllHref && events.length > 0 && (
                  <Link to={seeAllHref} className="ai-orb-seeall" onClick={() => setOpen(false)}>
                    See all results →
                  </Link>
                )}
              </>
            )}

            {!result && !error && (
              <p className="small muted ai-orb-hint">
                Ask in plain English — I'll pull matching networking events from the calendar.
              </p>
            )}
          </div>
        </div>
      )}

      <button
        ref={orbRef}
        type="button"
        className={open ? 'ai-orb is-open' : 'ai-orb'}
        aria-expanded={open}
        aria-label={open ? 'Close event finder' : 'Open event finder'}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="ai-orb-glow" aria-hidden="true" />
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
          {open ? (
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          ) : (
            <>
              <circle cx="11" cy="11" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" />
              <path d="M16 16l4 4" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
            </>
          )}
        </svg>
      </button>
    </div>
  );
}
